import { useMemo, useState } from "react";
import AdminHeader from "@/components/AdminHeader";
import QRCode from "@/components/QRCode";
import { API } from "@/lib/api";
import { Download, QrCode as QrIcon, Link as LinkIcon } from "lucide-react";

const SITE = typeof window !== "undefined" ? window.location.origin : "";

const PRESETS = [
  { id: "home", label: "Site — Accueil", path: "/", desc: "QR principal à imprimer sur flyers, cartes de visite, vitrines" },
  { id: "convoyage", label: "Service — Convoyage", path: "/convoyage", desc: "Directement vers la demande de convoyage" },
  { id: "vtc", label: "Service — VTC", path: "/vtc", desc: "Directement vers la réservation VTC" },
  { id: "track", label: "Suivi d'une réservation", path: "/suivi", desc: "Page de suivi client (référence à saisir)" },
];

export default function AdminQrCodes() {
  const [tab, setTab] = useState("home");
  const [customUrl, setCustomUrl] = useState("");
  const [ref, setRef] = useState("");

  const active = PRESETS.find((p) => p.id === tab);
  const url = useMemo(() => {
    if (tab === "custom") return customUrl.trim();
    if (tab === "ref") return ref.trim() ? `${SITE}/suivi/${ref.trim().toUpperCase()}` : "";
    return SITE + (active?.path || "/");
  }, [tab, active, customUrl, ref]);

  const filename = useMemo(() => {
    if (tab === "custom") return "das-simao-auto-custom.png";
    if (tab === "ref") return `das-simao-auto-suivi-${ref.trim().toUpperCase() || "ref"}.png`;
    return `das-simao-auto-${tab}.png`;
  }, [tab, ref]);

  const printableUrl = url
    ? `${API}/qr?data=${encodeURIComponent(url)}&size=32&bg=white&logo=1&download=1&filename=${encodeURIComponent(filename)}`
    : "#";

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-qr-page">
      <AdminHeader />
      <main className="max-w-6xl mx-auto pt-28 pb-16 px-6 lg:px-10">
        <div className="text-xs tracking-[0.5em] gold uppercase">Marketing</div>
        <h1 className="mt-2 font-serif text-white text-4xl">QR Codes</h1>
        <p className="mt-3 text-gray-400 max-w-2xl">
          Générez des QR codes prêts à imprimer (haute résolution, avec logo)
          pour vos flyers, tickets, cartes de visite ou vitrines. Chaque QR est
          téléchargeable en PNG.
        </p>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              data-testid={`qr-tab-${p.id}`}
              onClick={() => setTab(p.id)}
              className={`text-xs px-4 py-2 border tracking-wide transition-all ${
                tab === p.id
                  ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                  : "border-white/10 text-gray-400 hover:border-white/30"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            data-testid="qr-tab-ref"
            onClick={() => setTab("ref")}
            className={`text-xs px-4 py-2 border tracking-wide transition-all ${
              tab === "ref"
                ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                : "border-white/10 text-gray-400 hover:border-white/30"
            }`}
          >
            Suivi par référence
          </button>
          <button
            data-testid="qr-tab-custom"
            onClick={() => setTab("custom")}
            className={`text-xs px-4 py-2 border tracking-wide transition-all ${
              tab === "custom"
                ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                : "border-white/10 text-gray-400 hover:border-white/30"
            }`}
          >
            URL personnalisée
          </button>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
          {/* Preview */}
          <div className="bg-[#0d0d0d] border border-white/10 p-10 flex flex-col items-center">
            {tab === "custom" && (
              <div className="w-full mb-6">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                  URL personnalisée
                </div>
                <input
                  data-testid="qr-custom-input"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://exemple.fr/page"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[color:var(--gold)]"
                />
              </div>
            )}
            {tab === "ref" && (
              <div className="w-full mb-6">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                  Référence de réservation
                </div>
                <input
                  data-testid="qr-ref-input"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="DSA-ABC123"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-white tracking-[0.2em] focus:outline-none focus:border-[color:var(--gold)]"
                />
              </div>
            )}
            {url ? (
              <QRCode
                data={url}
                size={280}
                boxSize={14}
                showDownload={false}
                label={null}
                testId="qr-preview"
              />
            ) : (
              <div className="w-[300px] h-[300px] border border-dashed border-white/10 flex items-center justify-center text-gray-500 text-sm">
                Saisissez une valeur
              </div>
            )}
            {url && (
              <div className="mt-6 text-sm text-gray-400 inline-flex items-center gap-2">
                <LinkIcon size={14} className="gold" />
                <span className="break-all">{url}</span>
              </div>
            )}
          </div>

          {/* Actions & context */}
          <aside className="space-y-4 lg:sticky lg:top-28 h-fit">
            <div className="bg-[#0d0d0d] border border-white/10 p-6">
              <div className="text-xs tracking-widest gold uppercase mb-3">
                <QrIcon size={12} className="inline mr-2" />
                À télécharger
              </div>
              <div className="text-sm text-gray-400 leading-relaxed">
                {tab === "custom"
                  ? "QR code personnalisé — utile pour campagnes ponctuelles, salons, opérations spéciales."
                  : tab === "ref"
                    ? "Un QR unique pour un client donné. Idéal à imprimer sur un devis ou un ticket physique — il ouvre directement sa page de suivi."
                    : active?.desc}
              </div>
              <a
                data-testid="qr-download-btn"
                href={url ? printableUrl : "#"}
                onClick={(e) => !url && e.preventDefault()}
                className={`btn-gold w-full inline-flex items-center justify-center gap-2 mt-6 ${!url ? "pointer-events-none opacity-40" : ""}`}
              >
                <Download size={16} /> Télécharger PNG haute-résolution
              </a>
              <div className="text-xs text-gray-500 mt-3">
                Résolution ~1280×1280 px avec logo — parfait pour flyers A5, cartes de visite, vitrines.
              </div>
            </div>

            <div className="bg-[#0d0d0d] border border-white/10 p-6 text-sm text-gray-400 leading-relaxed">
              <div className="text-xs tracking-widest gold uppercase mb-3">Bon à savoir</div>
              <ul className="space-y-2 list-disc list-inside">
                <li>Le QR intègre votre logo au centre.</li>
                <li>Correction d'erreur haute (30%) — reste lisible même partiellement masqué.</li>
                <li>Testez toujours le QR avec votre téléphone avant impression.</li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
